import React from "react";
import {
  AbsoluteFill,
  Easing,
  Img,
  interpolate,
  staticFile,
  useCurrentFrame,
} from "remotion";
import { BRANDS } from "../../b2c/data/brands";
import { useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { Footnote, Kicker } from "../ui/Split";
import { useTiming } from "../timing";
import { Sfx, Ticks } from "../ui/Sfx";
import { usePop } from "../ui/Surfaces";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Proof — what it does for the first brands: up to 40 % of sales from
 * recommendation, −26 % acquisition cost on average (the blog's internal
 * figures, "piloter le bouche-à-oreille comme du paid"). Said as internal
 * and unaudited on screen, like the blog does. Then the brands already
 * live on Frak, from the explorer snapshot (`npm run sync:brands`), so the
 * logo strip never shows a brand that has left.
 */

/** Stats on scene beats 0.5 and 2, the brand logos from beat 4 (12-beat scene). */
const STAT_BEATS = [0.5, 2];
const LOGOS_BEAT = 4;
/** Frames a stat takes to count up (see `Stat`). */
const COUNT = 22;

const Stat: React.FC<{
  prefix?: string;
  value: number;
  sign?: string;
  label: string;
  at: number;
  size: number;
  /** Side-by-side stats: reserve the prefix line so the numbers align. */
  keepPrefixRoom?: boolean;
}> = ({
  prefix,
  value,
  sign = "",
  label,
  at,
  size,
  keepPrefixRoom = false,
}) => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const p = usePop(at);
  const v = interpolate(frame, [at, at + COUNT], [0, value], {
    ...clamp,
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  return (
    <div
      style={{
        flex: 1,
        opacity: Math.min(1, p * 2),
        translate: `0 ${(1 - p) * 40}px`,
      }}
    >
      {prefix || keepPrefixRoom ? (
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: size * 0.3,
            lineHeight: 1.2,
            color: LOOK.sky,
            marginBottom: -size * 0.04,
          }}
        >
          {prefix ?? "\u00A0"}
        </div>
      ) : null}
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: size,
          lineHeight: 1,
          letterSpacing: -size * 0.045,
          color: LOOK.white,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {sign}
        {Math.round(v)}
        {copy.pct}
      </div>
      <div
        style={{
          marginTop: 12,
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: 38,
          lineHeight: 1.2,
          color: LOOK.sky,
          maxWidth: 520,
          textWrap: "balance",
        }}
      >
        {label}
      </div>
    </div>
  );
};

export const Proof: React.FC = () => {
  const frame = useCurrentFrame();
  const copy = useCopy();
  const stage = useStage();
  const wide = stage.shape === "wide";
  const p = copy.proof;
  const logos = BRANDS.filter((b) => b.logo).slice(0, wide ? 9 : 6);
  const tile = wide ? 118 : 116;
  const { beat } = useTiming();
  const STAT_AT = STAT_BEATS.map((b) => Math.round(b * beat));
  const LOGOS_AT = Math.round(LOGOS_BEAT * beat);
  const drift = interpolate(frame, [LOGOS_AT, LOGOS_AT + 90], [0, -24]);

  return (
    <>
      <AbsoluteFill
        style={{
          background: LOOK.blue,
          padding: wide
            ? "0 140px"
            : stage.shape === "tall"
              ? "120px 80px"
              : "70px 80px",
          justifyContent: "center",
          alignItems: wide ? "flex-start" : "center",
          textAlign: wide ? "left" : "center",
        }}
      >
        <Kicker text={p.kicker} dark />
        <div
          style={{
            display: "flex",
            flexDirection: wide ? "row" : "column",
            gap: wide ? 80 : stage.shape === "tall" ? 50 : 34,
            alignSelf: "stretch",
            alignItems: wide ? "flex-start" : "center",
          }}
        >
          <Stat
            prefix={p.upTo}
            value={40}
            label={p.salesLabel}
            at={STAT_AT[0]}
            size={wide ? 190 : stage.shape === "tall" ? 170 : 140}
          />
          <Stat
            value={26}
            sign="−"
            label={p.cacLabel}
            keepPrefixRoom={wide}
            at={STAT_AT[1]}
            size={wide ? 190 : stage.shape === "tall" ? 170 : 140}
          />
        </div>

        {/* Brands already live on Frak. */}
        <div style={{ marginTop: wide ? 70 : 44, alignSelf: "stretch" }}>
          <div
            style={{
              fontFamily: LOOK.body,
              fontWeight: LOOK.semi,
              fontSize: 28,
              color: LOOK.blueSoft,
              marginBottom: 20,
              opacity: interpolate(
                frame,
                [LOGOS_AT - 6, LOGOS_AT],
                [0, 1],
                clamp,
              ),
            }}
          >
            {p.brands}
          </div>
          <div
            style={{
              display: "flex",
              gap: 20,
              justifyContent: wide ? "flex-start" : "center",
              translate: `${drift}px 0`,
            }}
          >
            {logos.map((b, i) => (
              <Logo
                key={b.slug}
                src={b.logo as string}
                size={tile}
                at={LOGOS_AT + i * 2}
              />
            ))}
          </div>
        </div>

        {/* "Internal, unaudited" is on screen as soon as the first figure is. */}
        <Footnote text={p.footnote} at={STAT_AT[0]} dark />
      </AbsoluteFill>
      {STAT_AT.map((at) => (
        <React.Fragment key={at}>
          <Ticks from={at} to={at + COUNT - 4} />
          <Sfx sound="pop" at={at + COUNT} volume={0.8} />
        </React.Fragment>
      ))}
      {/* Logos pop in one every 2 frames: a quick ripple of ticks. */}
      <Ticks
        from={LOGOS_AT}
        to={LOGOS_AT + logos.length * 2}
        every={2}
        volume={0.8}
      />
    </>
  );
};

const Logo: React.FC<{ src: string; size: number; at: number }> = ({
  src,
  size,
  at,
}) => {
  const p = usePop(at);
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: size * 0.24,
        overflow: "hidden",
        background: LOOK.white,
        boxShadow: "0 16px 30px -14px rgba(0, 10, 50, 0.6)",
        scale: String(p),
      }}
    >
      <Img
        src={staticFile(src)}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    </div>
  );
};
